import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo, { SITE_NAME, SITE_URL } from "@/components/base/Seo";
import SpotShowcase from "@/components/feature/SpotShowcase";
import { getTouristSpots, getSpotDistricts } from "@/services/touristSpotService";
import type { TouristSpot } from "@/services/touristSpotService";

export default function TouristSpotsPage() {
  const [spots, setSpots] = useState<TouristSpot[]>([]);
  const [districts, setDistricts] = useState<{ slug: string; name: string; count: number }[]>([]);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let active = true;
    Promise.all([getTouristSpots(), getSpotDistricts()])
      .then(([spotList, districtList]) => {
        if (!active) return;
        setSpots(spotList);
        setDistricts(districtList);
        setError("");
      })
      .catch(() => {
        if (!active) return;
        setSpots([]);
        setDistricts([]);
        setError("We could not load the tourist spots right now. Please try again in a moment.");
      });
    return () => {
      active = false;
    };
  }, [reloadToken]);

  return (
    <SiteLayout>
      <Seo
        title="Tourist Spots on the Canara Coast"
        description="Explore beaches, temples, waterfalls and heritage spots across South Canara, Udupi and North Canara. Discover places to visit on the Karnataka coast with timings, directions and businesses nearby."
        keywords="tourist places Karnataka coast, places to visit Mangaluru, Udupi tourist spots, North Canara sightseeing, coastal Karnataka beaches and temples"
        path="/tourist-spots"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Tourist Spots on the Canara Coast",
          url: `${SITE_URL}/tourist-spots`,
          isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
        }}
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="Breadcrumb">
            <Link to="/" className="cursor-pointer transition-colors hover:text-primary-700">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-foreground-700">Tourist Spots</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
            Tourist spots on the Canara coast
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            From quiet beaches and ancient temples to waterfalls and heritage landmarks — discover
            the places worth visiting across South Canara, Udupi and North Canara.
          </p>
        </div>
      </section>

      {error ? (
        <section className="mx-auto w-full max-w-[1400px] px-6 pt-8">
          <div className="flex items-start justify-between gap-3 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <span className="flex items-start gap-2.5">
              <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />
              {error}
            </span>
            <button
              type="button"
              onClick={() => setReloadToken((token) => token + 1)}
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
            >
              Retry
            </button>
          </div>
        </section>
      ) : null}

      <SpotShowcase spots={spots} districts={districts} />
    </SiteLayout>
  );
}